import { RuleCard } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import { RulePanelProps } from "../utils/types";

const RulePanel = ({
  title,
  subtitle,
  rules,
  onAddRule,
  onEditRule,
  onDeleteRule,
  searchQuery,
  onSearchChange,
  fullWidth = false,
}: RulePanelProps) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredRules = useMemo(
    () =>
      rules
        .map((text, originalIndex) => ({ text, originalIndex }))
        .filter(({ text }) =>
          text.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
    [rules, searchQuery],
  );

  const handleEditStart = (originalIndex: number, text: string) => {
    setEditingIndex(originalIndex);
    setEditingText(text);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const handleEditSave = () => {
    if (editingIndex !== null) {
      onEditRule(editingIndex, editingText);
      setEditingIndex(null);
    }
  };

  return (
    <div
      className={`flex flex-col gap-3 overflow-hidden rounded-lg border border-primaryGray-16 bg-white p-4 ${
        fullWidth ? "w-full" : "flex-1"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-h12 font-bold text-primaryGray-1">{title}</h3>
          <p className="text-tiny text-primaryGray-8">{subtitle}</p>
        </div>

        {/* Search */}
        <div className="relative flex-shrink-0">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search"
            className="h-8 w-36 rounded border border-primaryGray-16 px-2 pr-7 text-tiny outline-none focus:border-primaryGray-8"
          />
          <svg
            className="absolute right-2 top-1/2 -translate-y-1/2 text-primaryGray-8"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 13L9.1 9.1M10.3333 5.66667C10.3333 8.244 8.244 10.3333 5.66667 10.3333C3.08934 10.3333 1 8.244 1 5.66667C1 3.08934 3.08934 1 5.66667 1C8.244 1 10.3333 3.08934 10.3333 5.66667Z"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Add Rule Button */}
      <button
        onClick={onAddRule}
        className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-tertiaryBlue-4 py-2 text-sm font-semibold text-tertiaryBlue-4 transition-colors hover:bg-tertiaryBlue-12"
      >
        + Add Rule
      </button>

      {/* Rules List */}
      <div className="flex flex-1 flex-col gap-2 overflow-auto">
        {filteredRules.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-tiny text-primaryGray-8">
            {searchQuery ? "No rules match your search" : "No rules added yet"}
          </div>
        ) : (
          filteredRules.map(({ text, originalIndex }) =>
            editingIndex === originalIndex ? (
              <textarea
                key={`${title}-${originalIndex}-edit`}
                ref={textareaRef}
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onBlur={handleEditSave}
                rows={3}
                className="w-full resize-none rounded-lg border border-primaryGray-16 bg-primaryGray-16 p-3 text-h12 leading-relaxed text-primaryGray-1 shadow-sm outline-none"
              />
            ) : (
              <RuleCard
                key={`${title}-${originalIndex}`}
                index={originalIndex}
                content={text}
                onEdit={() => handleEditStart(originalIndex, text)}
                onDelete={() => onDeleteRule(originalIndex)}
              />
            ),
          )
        )}
      </div>
    </div>
  );
};

export default RulePanel;
